import { Club, ClubMessage, ClubPlayer, Group, Night, RecurringSession } from "@mixitone/models";
import { formatDate, type NextSessionResult, resolveNextSession, withStateToggle } from "@mixitone/util";
import { AuthController } from "controllers/AuthController";
import { PortalController } from "controllers/PortalController";
import portalFunctions from "lib/portalFunctions";
import { ClubController } from "./ClubController";

function isPortalMessageVisible(m: ClubMessage, now = new Date()): boolean {
  if (m.is_archived || m.is_deleted) return false;
  if (m.published_starts_at && new Date(m.published_starts_at) > now) return false;
  if (m.published_ends_at && new Date(m.published_ends_at) < now) return false;
  return true;
}

interface State {
  loadingGroups: boolean;
  loadingGames: boolean;
  loadingSessions: boolean;
  loadingSessionIntents: boolean;
  savingSessionIntent: boolean;
  loadingMessages: boolean;
  clubId: string;
  clubPlayer: ClubPlayer;
  club: Club;
  groups: Group[];
  group: Group;
  canUpdateGroup: boolean;
  updatingGroup: boolean;
  games: {
    date: string;
    games: {
      index: number;
      players: { name: string; group: Group }[];
    }[];
  }[];
  sessions: RecurringSession[];
  sessionIntents: Array<{
    recurringSessionId: string;
    sessionDate: string | null;
    attending: boolean | null;
    canEdit: boolean;
  }>;
  clubMessages: ClubMessage[];
  upcomingNights: Night[];
  nextSession: NextSessionResult | null;
}

interface Props {
  params: { clubId: string };
}

export class ClubDetailsPageController extends PortalController<State, Props> {
  get initialState(): State {
    return {
      loadingGroups: true,
      loadingGames: true,
      loadingSessions: true,
      loadingSessionIntents: true,
      savingSessionIntent: false,
      loadingMessages: true,
      sessions: [],
      sessionIntents: [],
      clubMessages: [],
      upcomingNights: [],
      nextSession: null,
      clubId: "",
      clubPlayer: new ClubPlayer({}),
      club: new Club({}),
      groups: [],
      group: new Group({}),
      canUpdateGroup: true,
      updatingGroup: false,
      games: [],
    };
  }

  async initialize(props: Props) {
    this.state.clubId = this.get(ClubController).state.clubId;
    this.state.clubPlayer = this.get(ClubController).state.clubPlayer;
    this.state.club = this.state.clubPlayer.club;
    await this.state.clubPlayer.ensureMembershipsLoaded();
    await this.loadSessions();
    await this.loadUpcomingNights();
    this.updateNextSession();
    try {
      await this.loadSessionIntents();
    } finally {
      this.state.loadingSessionIntents = false;
    }
    await this.loadMessages();
    await this.loadGroups();
    await this.loadGames();
  }

  @withStateToggle("loadingMessages")
  async loadMessages() {
    await this.state.club.club_messages.load();
    this.state.clubMessages = (this.state.club.club_messages as ClubMessage[]).filter((m: ClubMessage) =>
      isPortalMessageVisible(m),
    );
  }

  @withStateToggle("loadingSessions")
  async loadSessions() {
    this.state.sessions = await RecurringSession.query().eq("club_id", this.state.clubId).all();
    this.updateNextSession();
  }

  @withStateToggle("loadingSessions")
  async loadUpcomingNights() {
    try {
      this.state.upcomingNights = await Night.query()
        .eq("club_id", this.state.clubId)
        .gt("date", new Date())
        .orderBy("date", "asc")
        .all();
    } catch (error) {
      // Keep next-session fallback usable if an unexpected DB-side read path fails.
      console.error("Failed to load upcoming nights", error);
      this.state.upcomingNights = [];
    }
    this.updateNextSession();
  }

  updateNextSession() {
    this.state.nextSession = resolveNextSession({
      recurringSessions: this.state.sessions.map((session) => ({
        id: session.id!,
        weekday: session.weekday!,
        startTime: session.start_time ?? null,
        endTime: session.end_time ?? null,
        title: session.title,
      })),
      upcomingNights: this.state.upcomingNights.map((night) => ({
        id: night.id!,
        sessionId: night.session_id ?? null,
        startsAt: night.date!,
      })),
      recurringWeekdayBase: "monday-1",
    });
  }

  async loadSessionIntents() {
    const { intents } = await portalFunctions.getClubSessionIntents({
      clubPlayerId: this.state.clubPlayer.uniqueId,
      clubId: this.state.clubId,
    });
    this.state.sessionIntents = intents;
  }

  @withStateToggle("savingSessionIntent")
  async actionSetSessionIntent(recurringSessionId: string, attending: boolean) {
    await portalFunctions.setSessionIntent({
      clubPlayerId: this.state.clubPlayer.uniqueId,
      recurringSessionId,
      attending,
    });
    await this.loadSessionIntents();
  }

  @withStateToggle("savingSessionIntent")
  async actionClearSessionIntent(recurringSessionId: string) {
    await portalFunctions.setSessionIntent({
      clubPlayerId: this.state.clubPlayer.uniqueId,
      recurringSessionId,
      clear: true,
    });
    await this.loadSessionIntents();
  }

  @withStateToggle("loadingGroups")
  async loadGroups() {
    await this.state.club.groups.load();
    this.state.groups = this.state.club.groups;
    this.state.group = this.state.clubPlayer.group ?? this.state.groups[0];
  }

  @withStateToggle("loadingGames")
  async loadGames() {
    const games = await portalFunctions.getPlayerGames({
      clubPlayerId: this.state.clubPlayer.uniqueId,
    });
    this.state.games = games.games.reduce<State["games"]>((acc, game) => {
      const date = formatDate(game.started_at);
      if (acc[acc.length - 1]?.date !== date) {
        acc.push({ date, games: [] });
      }
      acc[acc.length - 1].games.push({
        index: game.index,
        players: game.players
          .filter((player) => player.id !== this.state.clubPlayer.uniqueId)
          .map((player) => {
            return {
              ...player,
              group: this.state.groups.find((group) => group.uniqueId === player.group_id) ?? new Group({}),
            };
          }),
      });

      return acc;
    }, []);
  }

  @withStateToggle("updatingGroup")
  async actionUpdateGroup(group: Group) {
    this.state.clubPlayer.group = group;
    await portalFunctions.updatePlayerGroup({
      clubPlayerId: this.state.clubPlayer.uniqueId,
      groupId: group.uniqueId,
    });
    await this.state.clubPlayer.reload();
    await this.state.clubPlayer.memberships.load();
    await this.state.clubPlayer.group.load();
    this.state.group = this.state.clubPlayer.group ?? this.state.groups[0];
  }
}
